var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
import capitalize from "@material-ui/core/utils/capitalize";
import { Box } from "../box";
import { GridIcon, UserIcon } from "../icon";
import { makeStyles } from "../styles";
import clsx from "../utils/clsx";
import { getCosmosDataProps } from "../utils/comsosCompat";
import { useAvatarContext } from "./avatar-context";
import { useAvatarImage } from "./useAvatarImage";
var AvatarComponentName = "CosmosAvatar";
var useAvatarStyles = makeStyles(function (theme) { return ({
    root: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        height: theme.typography.pxToRem(40),
        width: theme.typography.pxToRem(40),
        color: theme.palette.text.secondary,
        backgroundColor: theme.mixins.shadeOfColor("neutral", "lightest"),
        fontSize: theme.typography.pxToRem(16),
        fontWeight: theme.typography.fontWeightMedium,
        textTransform: "uppercase",
        "& > svg": {
            height: theme.typography.pxToRem(24),
            width: theme.typography.pxToRem(24),
        },
    },
    img: {
        width: "100%",
        height: "100%",
        textAlign: "center",
        // Handle non-square image. The property isn't supported by IE 11.
        objectFit: "cover",
        // Hide alt text.
        color: "transparent",
        // Hide the image broken icon, only works on Chrome.
        textIndent: 10000,
    },
    variantCircular: {
        borderRadius: "50%",
    },
    fitContain: {
        objectFit: "contain",
    },
    colorPrimary: {
        backgroundColor: theme.palette.primary[50] || theme.palette.primary.light,
        color: theme.palette.primary.main,
    },
    colorInfo: {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.dark,
    },
    colorWarning: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
    },
    colorError: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.dark,
    },
    colorSuccess: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
    },
    variantRounded: {
        borderRadius: 4,
        "&$sizeSmall, &$sizeXsmall": {
            borderRadius: 2,
        },
        "&$sizeLarge": {
            borderRadius: 6,
        },
        "&$sizeXlarge": {
            borderRadius: 8,
        },
    },
    variantSquare: {
        borderRadius: 0,
    },
    sizeXsmall: {
        height: theme.typography.pxToRem(24),
        width: theme.typography.pxToRem(24),
        fontSize: theme.typography.pxToRem(12),
        "& > svg": {
            height: theme.typography.pxToRem(16),
            width: theme.typography.pxToRem(16),
        },
    },
    sizeSmall: {
        height: theme.typography.pxToRem(32),
        width: theme.typography.pxToRem(32),
        fontSize: theme.typography.pxToRem(14),
        "& > svg": {
            height: theme.typography.pxToRem(16),
            width: theme.typography.pxToRem(16),
        },
    },
    sizeMedium: {},
    sizeLarge: {
        height: theme.typography.pxToRem(64),
        width: theme.typography.pxToRem(64),
        fontSize: theme.typography.pxToRem(24),
        "& > svg": {
            height: theme.typography.pxToRem(40),
            width: theme.typography.pxToRem(40),
        },
    },
    sizeXlarge: {
        height: theme.typography.pxToRem(128),
        width: theme.typography.pxToRem(128),
        fontSize: theme.typography.pxToRem(48),
        fontWeight: theme.typography.fontWeightRegular,
        "& > svg": {
            height: theme.typography.pxToRem(80),
            width: theme.typography.pxToRem(80),
        },
    },
    border: {
        border: "1px solid " + theme.palette.border.dark,
        "&$logo$hasImg": {
            backgroundColor: theme.palette.background.paper,
        },
    },
    logo: {
        backgroundColor: "transparent",
    },
    padding: {
        padding: theme.typography.pxToRem(theme.spacing(0.75)),
        "&$sizeSmall": {
            padding: theme.typography.pxToRem(theme.spacing(0.5)),
        },
        "&$sizeLarge": {
            padding: theme.typography.pxToRem(theme.spacing(1.5)),
        },
        "&$sizeXlarge": {
            padding: theme.typography.pxToRem(theme.spacing(3)),
        },
    },
    hasImg: {},
    placementBadge: {
        borderRadius: 8,
        border: "3px solid " + theme.palette.background.paper,
        "&$variantRounded&$sizeSmall": {
            borderRadius: 8,
        },
    },
}); }, { name: AvatarComponentName });
var getDefaultVariantForType = function (type) {
    switch (type) {
        case "logo":
            return "square";
        case "resource":
            return "rounded";
        case "user":
            return "circular";
    }
};
var fallbackIcon = {
    resource: GridIcon,
    user: UserIcon,
    logo: GridIcon,
};
export var Avatar = React.forwardRef(function (props, ref) {
    var classes = useAvatarStyles(props);
    var alt = props.alt, color = props.color, childrenProp = props.children, imgProps = props.imgProps, includePadding = props.includePadding, showBorder = props.showBorder, sizeProp = props.size, _a = props.imgFit, imgFit = _a === void 0 ? "cover" : _a, srcProp = props.src, fallbackIconOverride = props.fallbackIconOverride, typeProp = props.type, variantProp = props.variant, email = props.email, initials = props.initials, _b = props.placement, placement = _b === void 0 ? "default" : _b, 
    // remove to prevent passing through to the root
    propClasses = props.classes, rootProps = __rest(props, ["alt", "color", "children", "imgProps", "includePadding", "showBorder", "size", "imgFit", "src", "fallbackIconOverride", "type", "variant", "email", "initials", "placement", "classes"]);
    var ctx = useAvatarContext();
    var size = sizeProp || ctx.size;
    var type = typeProp || ctx.type || "resource";
    var variantValue = variantProp || ctx.variant;
    var _c = useAvatarImage({ src: srcProp, type: type, email: email }), src = _c.src, status = _c.status;
    var FallbackIcon = fallbackIconOverride || fallbackIcon[type];
    var variantForType = getDefaultVariantForType(type);
    var variant = variantValue != null ? (variantValue === "circle" ? "circular" : variantValue || "circular") : variantForType;
    var children = null;
    var hasImgNotFailing = !!src && status !== "error";
    if (hasImgNotFailing) {
        children = (React.createElement("img", __assign({}, getCosmosDataProps("image"), { alt: alt, src: src, className: clsx(classes.img, imgFit === "contain" && classes.fitContain) }, imgProps)));
    }
    else if (childrenProp != null) {
        children = childrenProp;
    }
    else if ((type === "user" || type === "logo") && initials) {
        children = initials.substring(0, 2);
    }
    else {
        children = React.createElement(FallbackIcon, __assign({}, getCosmosDataProps("icon")));
    }
    return (React.createElement(Box, __assign({}, { ref: ref }, getCosmosDataProps("avatar"), { className: clsx(classes.root, size && classes["size" + capitalize(size)], variant && classes["variant" + capitalize(variant)], color && classes["color" + capitalize(color)], type === "logo" && classes.logo, showBorder && classes.border, includePadding && type === "logo" && classes.padding, hasImgNotFailing && classes.hasImg, placement === "badge" && classes.placementBadge) }, rootProps), children));
});
